import { DOZER_CONFIG, type DozerConfig } from '../../config/dozer.config';
import { HAZARDS_CONFIG, type HazardsConfig } from '../../config/hazards.config';
import type { WorldConfig } from '../../config/world.config';
import { dozerParts, dozerSpan, drivingRows, heapRoute } from '../../dozer/dozerRules';
import { HazardKind, type HazardDefinition } from '../../hazards/HazardDefinition';
import { vehicleSettings } from '../../hazards/VehicleModel';
import {
  craneSwingTouchedLanes,
  hazardEventType,
  hazardKeyZ,
  hazardOccupancy,
  hazardRequirements,
  hazardStartLead,
  vehiclePath,
  type LaneStretch,
} from '../../hazards/hazardRules';
import { DEFAULT_OBSTACLES, ObstacleCatalog } from '../../obstacles/ObstacleCatalog';
import type { ObstacleDefinition } from '../../obstacles/ObstacleDefinition';
import { ObstacleType } from '../../obstacles/ObstacleType';
import { ConnectorRole, type ConnectorDefinition } from '../connectors/ConnectorDefinition';
import { SegmentKind, type FixedObstaclePlacement, type SegmentDefinition, type TrackArea } from '../segments/SegmentDefinition';
import { arcExitPose, arcRadius } from '../segments/segmentPath';
import { DEFAULT_LAYOUT_RULES, type LayoutRules } from '../terrain/LayoutRules';
import { dropZoneStretch, hazardStretches, obstacleFootprint, obstacleStretch, roofClimbOf, runUpStretch, stretchesOverlap, terrainStretches } from '../terrain/roofRunUp';
import { analyzeTerrain, type TerrainAnalysis } from '../terrain/terrainAnalysis';
import { EMPTY_TERRAIN, highestGroundIn, lowestCeilingIn } from '../terrain/terrainQueries';
import { buildLayoutRows, type LaneRequirement } from './layoutRows';
import { SequenceValidator, type RowHistory } from './SequenceValidator';

const SOCKET_ID_PATTERN = /^(SOCKET_(OBS|COL|DECO|EVT)_[A-Z0-9_]+|AD_[A-Z0-9_]+)$/;
const EPSILON = 1e-6;
const QUARTER_TURN = Math.PI / 2;

export interface SegmentValidationOptions {
  /** Physics-derived layout rules (default: shipped configuration). */
  readonly rules?: LayoutRules;
  /** Gameplay type of an obstacle id – for checking hand-designed layouts (default: DEFAULT_OBSTACLES). */
  readonly obstacleTypeOf?: (obstacleId: string) => ObstacleType | undefined;
  /** Moving hazard tuning – timelines and vehicle paths (default: shipped configuration). */
  readonly hazards?: HazardsConfig;
  /** Dozer set piece tuning – the driver's pace and speed (default: shipped configuration). */
  readonly dozer?: DozerConfig;
}

const DEFAULT_OBSTACLE_TYPES: ReadonlyMap<string, ObstacleType> = new Map(DEFAULT_OBSTACLES.map((o) => [o.id, o.type]));
const DEFAULT_OBSTACLE_DEPTHS: ReadonlyMap<string, number> = new Map(DEFAULT_OBSTACLES.map((o) => [o.id, ObstacleCatalog.depthOf(o)]));
const DEFAULT_OBSTACLE_DEFINITIONS: ReadonlyMap<string, ObstacleDefinition> = new Map(DEFAULT_OBSTACLES.map((o) => [o.id, o]));

/** Exit roles each segment kind must have (order irrelevant). */
const EXITS_BY_KIND: Readonly<Record<SegmentKind, readonly string[]>> = {
  STRAIGHT: ['OUT'],
  TURN_LEFT: ['LEFT'],
  TURN_RIGHT: ['RIGHT'],
  T_JUNCTION: ['LEFT', 'RIGHT'],
  CROSSING: ['LEFT', 'OUT', 'RIGHT'],
  CURVE_LEFT: ['OUT'],
  CURVE_RIGHT: ['OUT'],
};

/** Kinds with a junction pivot and turn input (curves turn without input). */
const JUNCTION_KINDS: ReadonlySet<SegmentKind> = new Set([SegmentKind.TurnLeft, SegmentKind.TurnRight, SegmentKind.TJunction, SegmentKind.Crossing]);

const near = (a: number, b: number) => Math.abs(a - b) < EPSILON;

/**
 * Static checks for segment data. Runs in unit tests and in dev builds at
 * startup. Later the same checks validate data extracted from Blender exports.
 */
export function validateSegmentDefinition(definition: SegmentDefinition, world: WorldConfig, options: SegmentValidationOptions = {}): string[] {
  const errors: string[] = [];
  const where = `[${definition.id}]`;
  const halfWidth = definition.width / 2;

  if (!/^SEG_[A-Z0-9]+(?:_[A-Z0-9]+)*$/.test(definition.id)) errors.push(`${where} id must start with SEG_ (upper snake case)`);
  if (!world.allowedSegmentLengths.includes(definition.length)) {
    errors.push(`${where} length ${definition.length} m not in allowed lengths ${world.allowedSegmentLengths.join('/')}`);
  }
  if (!near(definition.width, world.trackWidth)) errors.push(`${where} width must be ${world.trackWidth} m`);

  // ── Connectors ──
  if (definition.entry.role !== ConnectorRole.In || definition.entry.id !== 'CONNECTOR_IN') {
    errors.push(`${where} entry must be CONNECTOR_IN with role IN`);
  }
  const entry = definition.entry.transform;
  if (Math.abs(entry.x) + Math.abs(entry.y) + Math.abs(entry.z) + Math.abs(entry.yaw) > EPSILON) {
    errors.push(`${where} CONNECTOR_IN must be the segment origin (0,0,0, yaw 0)`);
  }
  const roles = definition.exits.map((e) => e.role).sort();
  const expectedRoles = [...EXITS_BY_KIND[definition.kind]].sort();
  if (roles.join(',') !== expectedRoles.join(',')) {
    errors.push(`${where} kind ${definition.kind} needs exits [${expectedRoles.join(', ')}], has [${roles.join(', ')}]`);
  }
  for (const exit of definition.exits) {
    if (exit.id !== `CONNECTOR_${exit.role}`) errors.push(`${where} exit ${exit.id} must be named CONNECTOR_${exit.role}`);
    if (exit.allowedNextProfiles.length === 0) errors.push(`${where} exit ${exit.id} allows no following segment`);
    const problem = checkExitGeometry(definition, exit);
    if (problem) errors.push(`${where} ${exit.id}: ${problem}`);
  }

  // ── Junction ──
  const isJunction = JUNCTION_KINDS.has(definition.kind);
  if (isJunction && !definition.junction) errors.push(`${where} junction kinds need a junction pivot`);
  if (!isJunction && definition.junction) errors.push(`${where} straight segments must not define a junction pivot`);
  if (definition.junction) {
    const p = definition.junction.pivot;
    if (!near(p.x, 0) || !near(p.y, 0) || !near(p.z, definition.length - halfWidth)) {
      errors.push(`${where} JUNCTION_PIVOT must be at (0, 0, length − width/2) = (0, 0, ${definition.length - halfWidth})`);
    }
    if (definition.obstacleSpawnPoints.length > 0) errors.push(`${where} junctions must not have obstacle spawn points`);
  }

  for (const problem of checkCurveAndEnclosure(definition)) errors.push(`${where} ${problem}`);

  // ── Lanes ──
  if (definition.lanes.length !== world.laneCount) errors.push(`${where} must define ${world.laneCount} lanes`);
  for (const lane of definition.lanes) {
    if (!near(lane.width, world.laneWidth)) errors.push(`${where} lane ${lane.id} width must be ${world.laneWidth}`);
    if (Math.abs(lane.centerX) + lane.width / 2 > halfWidth + EPSILON) errors.push(`${where} lane ${lane.id} exceeds track width`);
  }

  // ── Sockets ──
  const ids = new Set<string>();
  const sockets = [
    ...definition.obstacleSpawnPoints,
    ...definition.collectibleSpawnPoints,
    ...definition.decorationSockets,
    ...definition.dynamicEventSockets,
    ...definition.advertisingSlots,
  ];
  for (const socket of sockets) {
    if (ids.has(socket.id)) errors.push(`${where} duplicate socket id ${socket.id}`);
    ids.add(socket.id);
    if (!SOCKET_ID_PATTERN.test(socket.id)) errors.push(`${where} socket id ${socket.id} violates naming convention`);
  }
  // Gameplay sockets must lie on the drivable path (junctions: on the straight approach before the square).
  const pathEnd = definition.junction ? definition.length - 2 * halfWidth : definition.length;
  for (const point of [...definition.obstacleSpawnPoints, ...definition.collectibleSpawnPoints]) {
    if (point.position.z < -EPSILON || point.position.z > pathEnd + EPSILON) {
      errors.push(`${where} ${point.id} must lie between z = 0 and ${pathEnd}`);
    }
    const lane = definition.lanes[point.lane];
    if (!lane) errors.push(`${where} ${point.id} references unknown lane ${point.lane}`);
    else if (!near(point.position.x, lane.centerX)) errors.push(`${where} ${point.id} is not on its lane center`);
  }
  for (const rowId of [...definition.obstacleRowLayouts.sparse, ...definition.obstacleRowLayouts.dense]) {
    if (!definition.obstacleSpawnPoints.some((p) => p.row === rowId)) errors.push(`${where} row layout references unknown row ${rowId}`);
  }
  // Set-piece rewards (golden bottle): on a collectible point of their own – never part of a bottle line.
  const rewardPoints = new Set<string>();
  for (const reward of definition.fixedCollectibles ?? []) {
    const point = definition.collectibleSpawnPoints.find((p) => p.id === reward.spawnPointId);
    if (!point) errors.push(`${where} fixed collectible on unknown spawn point ${reward.spawnPointId}`);
    else if (point.line !== undefined) errors.push(`${where} fixed collectible ${reward.spawnPointId} must not lie on the bottle line ${point.line}`);
    if (rewardPoints.has(reward.spawnPointId)) errors.push(`${where} spawn point ${reward.spawnPointId} carries two fixed collectibles`);
    rewardPoints.add(reward.spawnPointId);
    if (reward.scale !== undefined && !(reward.scale > 0)) errors.push(`${where} fixed collectible ${reward.spawnPointId} needs a positive scale`);
  }
  // Set-piece schedule: ascending stretches of marks, the first mark at minDistance, windows shorter than the spacing.
  const schedule = definition.generation.schedule;
  if (schedule) {
    const { stretches } = schedule;
    if (stretches.length === 0) errors.push(`${where} set-piece schedule needs at least one stretch`);
    if (stretches[0] && stretches[0].from !== definition.generation.minDistance) errors.push(`${where} the first schedule mark must be minDistance`);
    if (!(schedule.window > 0)) errors.push(`${where} schedule window must be positive`);
    if (!(schedule.forceAfter >= 0 && schedule.forceAfter < schedule.window)) errors.push(`${where} schedule forceAfter must lie inside the window`);
    stretches.forEach((stretch, i) => {
      if (!(stretch.every > 0)) errors.push(`${where} schedule stretch ${i + 1} needs a positive spacing`);
      if (schedule.window >= stretch.every) errors.push(`${where} schedule window reaches the next mark of stretch ${i + 1}`);
      if (stretch.until !== undefined && stretch.until < stretch.from) errors.push(`${where} schedule stretch ${i + 1} ends before it starts`);
      if (stretch.until === undefined && i < stretches.length - 1) errors.push(`${where} only the last schedule stretch may be endless`);
      const before = stretches[i - 1];
      if (before && stretch.from < (before.until ?? Infinity) + schedule.window) errors.push(`${where} schedule stretch ${i + 1} overlaps the one before`);
    });
  }
  // Fixed obstacles and variants are checked in validateLayouts (spawn points, types, playability).
  if (definition.collapseEvent) {
    const fixed = new Set((definition.fixedObstacles ?? []).map((f) => f.spawnPointId));
    if (!definition.dynamicEventSockets.some((s) => s.id === definition.collapseEvent?.socketId)) {
      errors.push(`${where} collapse event references unknown socket ${definition.collapseEvent.socketId}`);
    }
    for (const piece of definition.collapseEvent.pieces) {
      if (!fixed.has(piece.spawnPointId)) errors.push(`${where} collapse piece ${piece.spawnPointId} has no fixed obstacle`);
    }
  }
  // Overhead banners (ad gantry) hang above everything a runner can reach: highest ground + double jump + body.
  const reach = overheadReach(definition, options.rules ?? DEFAULT_LAYOUT_RULES);
  for (const slot of definition.advertisingSlots) {
    const besideTrack = Math.abs(slot.position.x) >= halfWidth;
    const beyondEnd = slot.position.z > definition.length || slot.position.z < 0;
    const overhead = slot.position.y - slot.height / 2 >= reach - EPSILON;
    if (!besideTrack && !beyondEnd && !overhead) errors.push(`${where} ad slot ${slot.id} must lie outside the drivable track or above ${reach} m`);
    if (!(slot.width > 0 && slot.height > 0)) errors.push(`${where} ad slot ${slot.id} needs a positive size`);
  }
  for (const socket of definition.decorationSockets) {
    // Footprint half extents in segment space (width runs along the socket's local x).
    const cos = Math.abs(Math.cos(socket.yaw));
    const sin = Math.abs(Math.sin(socket.yaw));
    const { width, depth } = socket.maxFootprint;
    const extentX = (cos * width + sin * depth) / 2;
    const extentZ = (sin * width + cos * depth) / 2;
    const besideTrack = Math.abs(socket.position.x) - extentX >= halfWidth;
    const beyondEnd = socket.position.z - extentZ >= definition.length || socket.position.z + extentZ <= 0;
    if (!besideTrack && !beyondEnd) errors.push(`${where} decoration socket ${socket.id} footprint reaches into the track`);
  }

  const rules = options.rules ?? DEFAULT_LAYOUT_RULES;
  const typeOf = options.obstacleTypeOf ?? ((id: string) => DEFAULT_OBSTACLE_TYPES.get(id));
  const hazardsConfig = options.hazards ?? HAZARDS_CONFIG;
  const analysis = analyzeTerrain(definition, rules);
  for (const error of [
    ...validateTerrainZones(definition, rules),
    ...analysis.issues,
    ...validateSpawnPointsOnTerrain(definition, analysis, rules),
    ...validateHazards(definition, analysis, rules, hazardsConfig),
    ...validateLayouts(definition, analysis, rules, typeOf),
    ...validateRoofRunUps(definition, rules, hazardsConfig),
    ...validateDozer(definition, rules, options.dozer ?? DOZER_CONFIG),
  ]) {
    errors.push(`${where} ${error}`);
  }
  return errors;
}

/** Highest point a runner reaches on the segment: top of its highest walkable surface or slab + jump + double jump + body. */
export function overheadReach(definition: SegmentDefinition, rules: LayoutRules): number {
  const { surfaces, ceilings } = definition.terrain ?? EMPTY_TERRAIN;
  const slabTops = ceilings.flatMap((c) => (c.top === undefined ? [] : [c.top]));
  const ground = Math.max(rules.groundHeight, ...surfaces.map((s) => Math.max(s.heightFrom, s.heightTo)), ...slabTops);
  return ground + rules.jumpHeight + rules.doubleJumpHeight + rules.standingHeight;
}

/** Zone naming, extents, ramp slopes; zones never cut a lane's running line in half. */
function validateTerrainZones(definition: SegmentDefinition, rules: LayoutRules): string[] {
  const errors: string[] = [];
  const terrain = definition.terrain ?? EMPTY_TERRAIN;
  const halfWidth = definition.width / 2;
  const half = rules.playerWidth / 2;
  const zones: (readonly [string, TrackArea & { readonly id: string }])[] = [
    ...terrain.surfaces.map((zone) => ['SURF', zone] as const),
    ...terrain.gaps.map((zone) => ['GAP', zone] as const),
    ...terrain.ceilings.map((zone) => ['CEIL', zone] as const),
  ];
  if (definition.junction && zones.length > 0) errors.push('junction modules must not have terrain');

  const ids = new Set<string>();
  for (const [prefix, zone] of zones) {
    if (!new RegExp(`^${prefix}_[A-Z0-9_]+$`).test(zone.id)) errors.push(`terrain ${zone.id} must be named ${prefix}_<NAME>`);
    if (ids.has(zone.id)) errors.push(`duplicate terrain id ${zone.id}`);
    ids.add(zone.id);
    if (!(zone.zTo > zone.zFrom && zone.xTo > zone.xFrom)) errors.push(`terrain ${zone.id} has an empty area`);
    if (zone.zFrom < -EPSILON || zone.zTo > definition.length + EPSILON) errors.push(`terrain ${zone.id} must lie within z = 0 … ${definition.length}`);
    if (zone.xTo <= -halfWidth || zone.xFrom >= halfWidth) errors.push(`terrain ${zone.id} does not touch the track`);
    for (const lane of definition.lanes) {
      for (const edge of [zone.xFrom, zone.xTo]) {
        if (edge > lane.centerX - half + EPSILON && edge < lane.centerX + half - EPSILON) {
          errors.push(`terrain ${zone.id}: edge x = ${edge} cuts through the running line of lane ${lane.index}`);
        }
      }
    }
  }
  for (const surface of terrain.surfaces) {
    const slope = Math.abs(surface.heightTo - surface.heightFrom) / Math.max(surface.zTo - surface.zFrom, EPSILON);
    if (slope > rules.maxRampSlope + EPSILON) errors.push(`ramp ${surface.id} is too steep (${slope.toFixed(2)} > ${rules.maxRampSlope})`);
    if (surface.xFrom < -halfWidth - EPSILON || surface.xTo > halfWidth + EPSILON) errors.push(`surface ${surface.id} must lie on the track`);
  }
  for (const gap of terrain.gaps) {
    if (!(gap.depth > 0)) errors.push(`pit ${gap.id} needs a positive depth`);
    if (gap.xFrom < -halfWidth - EPSILON || gap.xTo > halfWidth + EPSILON) errors.push(`pit ${gap.id} must lie on the track`);
  }
  for (const ceiling of terrain.ceilings) {
    if (ceiling.clearance < rules.groundHeight - EPSILON) errors.push(`ceiling ${ceiling.id} lies below the ground`);
  }
  return errors;
}

/**
 * Spawn points must suit the terrain: obstacles stand on the ground, away from pits,
 * with enough head room for the action they need; no lane-change obstacles where walls
 * between the lanes prevent changing lanes; collectibles never inside solid geometry.
 */
function validateSpawnPointsOnTerrain(definition: SegmentDefinition, analysis: TerrainAnalysis, rules: LayoutRules): string[] {
  const errors: string[] = [];
  const terrain = definition.terrain ?? EMPTY_TERRAIN;
  const base = rules.groundHeight;
  const half = rules.playerWidth / 2;
  const clearance = rules.spawnClearance;

  for (const point of definition.obstacleSpawnPoints) {
    const lane = definition.lanes[point.lane];
    if (!lane) continue;
    const minX = lane.centerX - half;
    const maxX = lane.centerX + half;
    const z = point.position.z;
    const ground = highestGroundIn(terrain, base, z - EPSILON, z + EPSILON, minX, maxX);
    if (ground === null || Math.abs(ground - point.position.y) > EPSILON) {
      errors.push(`${point.id} must stand on the ground (y = ${ground ?? 'pit'})`);
      continue;
    }
    for (let s = z - clearance; s < z + clearance - EPSILON; s += rules.sampleStep) {
      if (highestGroundIn(terrain, base, s, Math.min(s + rules.sampleStep, z + clearance), minX, maxX) === null) {
        errors.push(`${point.id} is closer than ${clearance} m to a pit`);
        break;
      }
    }
    const ceiling = lowestCeilingIn(terrain, z - clearance, z + clearance, minX, maxX, -Infinity, 'all');
    const headroom = ceiling === null ? Infinity : ceiling - ground;
    const needed: Partial<Record<ObstacleType, number>> = {
      [ObstacleType.Jump]: rules.standingHeight + rules.jumpHeight,
      [ObstacleType.DoubleJump]: rules.standingHeight + rules.jumpHeight + rules.doubleJumpHeight,
      [ObstacleType.Slide]: rules.standingHeight,
    };
    if (headroom < rules.slidingHeight) errors.push(`${point.id} lies inside a wall`);
    for (const type of point.allowedTypes) {
      const room = needed[type];
      if (room !== undefined && headroom < room - EPSILON) errors.push(`${point.id}: ${type} needs ${room} m head room, has ${headroom.toFixed(2)} m`);
    }
    const walled = analysis.wallStretches.some((stretch) => z > stretch.zFrom - EPSILON && z < stretch.zTo + EPSILON);
    if (walled && point.allowedTypes.includes(ObstacleType.LaneChange)) {
      errors.push(`${point.id}: no LANE_CHANGE obstacles where walls between the lanes prevent changing lanes`);
    }
    // The fairness check of a double-jump step (container roof, walkable pipe crown) relies on a free run-up in its lane.
    for (const climb of [...analysis.climbs, ...analysis.decks.flatMap((deck) => (deck.climb ? [deck.climb] : []))]) {
      if (climb.lane === point.lane && z < climb.z && z > climb.z - climb.runUpNeeded - clearance - EPSILON) {
        errors.push(`${point.id} stands in the run-up of the double-jump step at z=${climb.z.toFixed(2)} (keep ${climb.runUpNeeded.toFixed(1)} m free)`);
      }
    }
    // A runner dropping off a walkable top lands on free road and has one reaction time before the next obstacle.
    for (const deck of analysis.decks) {
      if (deck.lane === point.lane && z > deck.zTo - EPSILON && z < deck.zTo + deck.dropZone + clearance - EPSILON) {
        errors.push(`${point.id} stands in the drop zone of the walkable top ending at z=${deck.zTo.toFixed(2)} (keep ${deck.dropZone.toFixed(1)} m free)`);
      }
    }
  }

  for (const point of definition.collectibleSpawnPoints) {
    const { x, y, z } = point.position;
    // A bottle above a slab's walkable top (pipe crown) sits on it – the slab is its ground, not its ceiling.
    const ground = highestGroundIn(terrain, base, z - EPSILON, z + EPSILON, x - EPSILON, x + EPSILON, y);
    if (ground !== null && y < ground - EPSILON) errors.push(`${point.id} lies inside the ground`);
    const ceiling = lowestCeilingIn(terrain, z - EPSILON, z + EPSILON, x - EPSILON, x + EPSILON, -Infinity, 'all', y);
    if (ceiling !== null && (y >= ceiling - EPSILON || ceiling - (ground ?? base) < rules.slidingHeight)) {
      errors.push(`${point.id} lies inside a ceiling or wall`);
    }
  }
  return errors;
}

/**
 * Moving hazards: naming, socket and lanes; their timeline must fit into the socket's trigger
 * distance at top speed; cranes and excavators stand beside the track and reach their spot;
 * vehicles drive inside the segment and finish a lane change before they meet the runner;
 * nothing static stands where a hazard moves or lands; key lines stay apart from obstacle and
 * terrain rows (they are decision rows of their own).
 */
function validateHazards(definition: SegmentDefinition, analysis: TerrainAnalysis, rules: LayoutRules, config: HazardsConfig): string[] {
  const errors: string[] = [];
  const hazards = definition.hazards ?? [];
  const laneCount = definition.lanes.length;
  const halfWidth = definition.width / 2;
  const ids = new Set<string>();
  for (const hazard of hazards) {
    const name = `hazard ${hazard.id}`;
    if (!/^HAZ_[A-Z0-9_]+$/.test(hazard.id)) errors.push(`${name} must be named HAZ_<NAME>`);
    if (ids.has(hazard.id)) errors.push(`duplicate hazard id ${hazard.id}`);
    ids.add(hazard.id);
    for (const lane of [...hazardRequirements(hazard, laneCount).map((r) => r.lane)]) {
      if (!definition.lanes[lane]) errors.push(`${name} references unknown lane ${lane}`);
    }
    const keyZ = hazardKeyZ(hazard);
    if (keyZ < rules.connectorClearance || keyZ > definition.length - rules.connectorClearance) {
      errors.push(`${name}: key line z=${keyZ} must lie ${rules.connectorClearance} m away from IN/OUT`);
    }

    const socket = definition.dynamicEventSockets.find((s) => s.id === hazard.socketId);
    if (!socket) {
      errors.push(`${name} references unknown socket ${hazard.socketId}`);
    } else {
      const event = hazardEventType(hazard);
      if (!socket.supportedEvents.includes(event)) errors.push(`${name}: socket ${socket.id} must support ${event}`);
      // Armed no later than `startLead` seconds before the runner arrives – at top speed.
      const needed = rules.sequence.maxSpeed * hazardStartLead(hazard, config) - (keyZ - socket.position.z);
      if (socket.triggerDistance < needed - EPSILON) {
        errors.push(`${name}: socket ${socket.id} triggers at ${socket.triggerDistance} m, the timeline needs ${needed.toFixed(1)} m at top speed`);
      }
    }
    errors.push(...validateHazardGeometry(definition, hazard, config, halfWidth, rules.playerWidth).map((e) => `${name}: ${e}`));

    for (const row of [...analysis.rows.map((r) => r.z), ...definition.obstacleSpawnPoints.map((p) => p.position.z)]) {
      if (Math.abs(row - keyZ) <= rules.rowMergeDistance + EPSILON) {
        errors.push(`${name}: key line z=${keyZ} too close to the row at z=${row} – keep decision rows apart`);
        break;
      }
    }
  }

  const layouts: { readonly id: string; readonly placements: readonly FixedObstaclePlacement[]; readonly hazards?: readonly string[] }[] = [
    ...(definition.fixedObstacles ? [{ id: 'fixedObstacles', placements: definition.fixedObstacles }] : []),
    ...(definition.obstacleVariants ?? []),
  ];
  for (const layout of layouts) {
    for (const id of layout.hazards ?? hazards.map((h) => h.id)) {
      const hazard = hazards.find((h) => h.id === id);
      if (!hazard) continue; // reported by validateLayouts
      const occupied = hazardOccupancy(hazard, config, laneCount);
      for (const placement of layout.placements) {
        const point = definition.obstacleSpawnPoints.find((p) => p.id === placement.spawnPointId);
        if (!point) continue;
        const half = (DEFAULT_OBSTACLE_DEPTHS.get(placement.obstacleId) ?? 0) / 2;
        const z = point.position.z;
        const clash = occupied.some((s) => s.lane === point.lane && z + half > s.zFrom - rules.spawnClearance && z - half < s.zTo + rules.spawnClearance);
        if (clash) errors.push(`${layout.id}: ${placement.obstacleId} on ${point.id} stands in the way of ${hazard.id}`);
      }
    }
  }
  return errors;
}

function validateHazardGeometry(definition: SegmentDefinition, hazard: HazardDefinition, config: HazardsConfig, halfWidth: number, playerWidth: number): string[] {
  const errors: string[] = [];
  const laneX = (lane: number) => definition.lanes[lane]?.centerX ?? 0;
  switch (hazard.kind) {
    case HazardKind.CraneBeam:
    case HazardKind.CraneBlock:
    case HazardKind.CraneSwing: {
      // The swinging block's trolley travels either side of the track centre.
      const loadX = hazard.kind === HazardKind.CraneBlock ? laneX(hazard.lane) : 0;
      const travel = hazard.kind === HazardKind.CraneSwing ? config.craneSwing.trolleyTravel : 0;
      if (Math.abs(hazard.crane.x) <= halfWidth) errors.push('the crane must stand beside the track');
      for (const trolleyX of new Set([loadX - travel, loadX + travel])) {
        const reach = Math.hypot(trolleyX - hazard.crane.x, hazard.z - hazard.crane.z);
        if (reach < config.crane.minReach || reach > config.crane.maxReach) {
          errors.push(`crane trolley reach ${reach.toFixed(1)} m outside ${config.crane.minReach}–${config.crane.maxReach} m`);
        }
      }
      if (hazard.kind === HazardKind.CraneBeam && !(hazard.clearance > config.craneBeam.rig.loadBottom)) errors.push('beam clearance must be positive');
      if (hazard.kind === HazardKind.CraneSwing) {
        // Its row in the sequence validator blocks exactly its lane: around the runner's arrival it is over that one only.
        const { blockWindow } = config.craneSwing;
        const lanes = definition.lanes.map((l) => l.centerX);
        const touched = craneSwingTouchedLanes(hazard, config.craneSwing, lanes, playerWidth, -blockWindow, blockWindow);
        if (touched.length !== 1 || touched[0] !== hazard.lane) {
          errors.push(`the swinging load touches lanes [${touched.join(', ')}] within ±${blockWindow} s of the runner's arrival – it must block lane ${hazard.lane} only`);
        }
      }
      break;
    }
    case HazardKind.Excavator: {
      const { rig } = config.excavator;
      if (Math.abs(hazard.machine.x) - rig.tailRadius < halfWidth) errors.push('the excavator must stand beside the track (tail swing)');
      if (Math.sign(hazard.machine.x) !== Math.sign(laneX(hazard.lane))) errors.push('the excavator dumps into the outer lane on its own side');
      const maxReach = rig.boomPivot.forward + Math.hypot(rig.stickPivot.forward, rig.stickPivot.up) + Math.hypot(rig.bucketPivot.forward, rig.bucketPivot.up);
      if (Math.hypot(laneX(hazard.lane) - hazard.machine.x, hazard.z - hazard.machine.z) > maxReach) errors.push('the dump spot is out of reach');
      break;
    }
    case HazardKind.Vehicle: {
      const path = vehiclePath(hazard, config);
      const reachesOut = (s: LaneStretch) => s.zFrom < -EPSILON || s.zTo > definition.length + EPSILON;
      if (path.some(reachesOut)) errors.push('the vehicle must drive inside the segment (start + rear, stop − front)');
      const settings = vehicleSettings(config, hazard.vehicle);
      if (hazard.startZ - (hazard.meetZ + settings.front) < (settings.speed * settings.accelerationTime) / 2) errors.push('start too close to the meeting line');
      if (hazard.stopZ >= hazard.meetZ + settings.front) errors.push('the vehicle must stop behind the meeting line');
      if (hazard.zigzag) {
        if (hazard.laneChange) errors.push('a zig-zag needs no lane change');
        if (hazard.zigzag.crossing < 4) errors.push('a zig-zag crosses the road over at least 4 m of its path');
      }
      const change = hazard.laneChange;
      if (change) {
        if (Math.abs(change.toLane - hazard.lane) !== 1) errors.push('a lane change goes to a neighbouring lane');
        if (change.atZ > hazard.startZ || change.atZ - settings.laneChangeLength < hazard.meetZ + settings.front) {
          errors.push('the lane change must happen between start and meeting line');
        }
      }
      break;
    }
  }
  return errors;
}

/**
 * Hand-designed layouts (fixed obstacles, variants) are checked together with the terrain
 * rows and the key lines of their moving hazards by the SequenceValidator – at start and at
 * top speed, from a standing start.
 */
function validateLayouts(
  definition: SegmentDefinition,
  analysis: TerrainAnalysis,
  rules: LayoutRules,
  typeOf: (obstacleId: string) => ObstacleType | undefined,
): string[] {
  const errors: string[] = [];
  const allHazards = (definition.hazards ?? []).map((h) => h.id);
  const layouts: { readonly id: string; readonly placements: readonly FixedObstaclePlacement[]; readonly hazards?: readonly string[] }[] = [
    ...(definition.fixedObstacles ? [{ id: 'fixedObstacles', placements: definition.fixedObstacles }] : []),
    ...(definition.obstacleVariants ?? []),
  ];
  // Moving hazards without designed layouts still have to be playable on their own.
  if (layouts.length === 0 && allHazards.length > 0) layouts.push({ id: 'hazards', placements: [] });
  const variantIds = new Set<string>();
  const collectibleLines = new Set(definition.collectibleSpawnPoints.flatMap((p) => (p.line === undefined ? [] : [p.line])));
  for (const variant of definition.obstacleVariants ?? []) {
    if (variantIds.has(variant.id)) errors.push(`duplicate obstacle variant ${variant.id}`);
    variantIds.add(variant.id);
    if (!(variant.weight > 0)) errors.push(`obstacle variant ${variant.id} needs a positive weight`);
    for (const line of variant.collectibleLines ?? []) {
      if (!collectibleLines.has(line)) errors.push(`obstacle variant ${variant.id}: unknown collectible line ${line}`);
    }
  }
  if (definition.obstacleVariants && definition.fixedObstacles) errors.push('use either fixedObstacles or obstacleVariants');

  const { sequence } = rules;
  for (const layout of layouts) {
    const requirements: LaneRequirement[] = [];
    const used = new Set<string>();
    for (const placement of layout.placements) {
      const point = definition.obstacleSpawnPoints.find((p) => p.id === placement.spawnPointId);
      const type = typeOf(placement.obstacleId);
      if (used.has(placement.spawnPointId)) errors.push(`${layout.id}: spawn point ${placement.spawnPointId} used twice`);
      used.add(placement.spawnPointId);
      if (!point) {
        errors.push(`${layout.id}: unknown spawn point ${placement.spawnPointId}`);
        continue;
      }
      if (!type) {
        errors.push(`${layout.id}: unknown obstacle ${placement.obstacleId}`);
        continue;
      }
      if (!point.allowedTypes.includes(type)) errors.push(`${layout.id}: ${placement.obstacleId} (${type}) is not allowed on ${point.id}`);
      requirements.push({ z: point.position.z, lane: point.lane, type });
    }
    for (const id of layout.hazards ?? allHazards) {
      const hazard = definition.hazards?.find((h) => h.id === id);
      if (!hazard) errors.push(`${layout.id}: unknown hazard ${id}`);
      else requirements.push(...hazardRequirements(hazard, definition.lanes.length));
    }
    const rows = buildLayoutRows(analysis.rows, requirements, definition.lanes.length, rules.rowMergeDistance);
    const steps = [
      ...analysis.wallStretches.filter((s) => s.zTo < definition.length).map((s) => ({ z: s.zTo, lanes: null })),
      ...rows,
    ].sort((a, b) => a.z - b.z);
    for (const speed of [sequence.minSpeed, sequence.maxSpeed]) {
      const validator = new SequenceValidator({
        laneChangeDuration: sequence.laneChangeDuration,
        reactionTime: sequence.reactionTime,
        maxAirTime: sequence.maxAirTime,
        speedAt: () => speed,
      });
      let history: RowHistory | null = null;
      for (const step of steps) {
        if (!step.lanes) {
          history = SequenceValidator.wallsEnd(history, step.z);
          continue;
        }
        const result = validator.validate({ distance: step.z, lanes: step.lanes }, history);
        if (!result.ok) errors.push(`${layout.id}: row at z=${step.z.toFixed(2)} unplayable at ${speed} m/s (${result.violations.join('; ')})`);
        history = result.history;
      }
    }
  }
  return errors;
}

/**
 * Dozer set piece (SEG_DOZER_60M): the parked dozer stands on its spawn point in every layout, the pit crosses every
 * lane ahead of its blade, and every layout is fair for the driver – the SequenceValidator with the dozer's steering
 * and speed at both ends of the speed ramp, from the parked dozer (heaps are pushed: free; everything else stops the
 * blade: a lane change – the dozer cannot jump) – with a route through every heap (the "perfect" run) and earth
 * enough to fill the pit.
 */
function validateDozer(definition: SegmentDefinition, rules: LayoutRules, config: DozerConfig): string[] {
  const setPiece = definition.dozer;
  if (!setPiece) return [];
  const errors: string[] = [];
  const { point, pit } = dozerParts(definition);
  if (!point) errors.push(`dozer: unknown spawn point ${setPiece.spawnPointId}`);
  if (!pit) errors.push(`dozer: unknown pit ${setPiece.pitGapId}`);
  if (!point || !pit) return errors;
  const half = definition.width / 2;
  if (pit.xFrom > -half + EPSILON || pit.xTo < half - EPSILON) errors.push(`dozer: the pit ${pit.id} must cross every lane`);
  const blade = point.position.z + dozerSpan(config.vehicle.colliders).front;
  if (pit.zFrom <= blade) errors.push('dozer: the pit must lie ahead of the parked dozer');
  const layouts = [...(definition.fixedObstacles ? [{ id: 'fixedObstacles', placements: definition.fixedObstacles }] : []), ...(definition.obstacleVariants ?? [])];
  if (layouts.length === 0) errors.push('dozer: needs designed layouts that place the dozer');
  const { sequence } = rules;
  for (const layout of layouts) {
    if (!layout.placements.some((p) => p.spawnPointId === point.id && p.obstacleId === config.vehicle.obstacleId)) {
      errors.push(`${layout.id}: the dozer (${config.vehicle.obstacleId}) must stand on ${point.id}`);
    }
    const rows = drivingRows(definition, layout.placements, (id) => DEFAULT_OBSTACLE_DEFINITIONS.get(id), config);
    const heaps = rows.reduce((count, row) => count + row.heaps.length, 0);
    if (heaps < config.pit.fillThreshold) errors.push(`${layout.id}: ${heaps} heaps – filling the pit needs ${config.pit.fillThreshold}`);
    for (const runSpeed of [sequence.minSpeed, sequence.maxSpeed]) {
      const speed = runSpeed * config.drive.speedFactor;
      const validator = new SequenceValidator({ laneChangeDuration: config.drive.laneChangeDuration, reactionTime: sequence.reactionTime, maxAirTime: 0, speedAt: () => speed });
      let history: RowHistory | null = { distance: blade, safeLanes: [point.lane], forcedDoubleJump: false };
      for (const row of rows) {
        const result = validator.validate({ distance: row.z, lanes: row.lanes }, history);
        if (!result.ok) errors.push(`${layout.id}: driving row at z=${row.z.toFixed(2)} unfair at ${speed.toFixed(1)} m/s (${result.violations.join('; ')})`);
        history = result.history;
      }
      if (!heapRoute(rows, point.lane, blade, speed, config.drive.laneChangeDuration, sequence.reactionTime).feasible) {
        errors.push(`${layout.id}: no route through every heap at ${speed.toFixed(1)} m/s`);
      }
    }
  }
  return errors;
}

/** Exits must sit exactly where the path model expects them (equal path length to every exit). */
function checkExitGeometry(definition: SegmentDefinition, exit: ConnectorDefinition): string | null {
  const t = exit.transform;
  const halfWidth = definition.width / 2;
  const pivotZ = definition.length - halfWidth;
  switch (exit.role) {
    case ConnectorRole.Out: {
      if (definition.curve) {
        const end = arcExitPose(definition.length, definition.curve.angle);
        return near(t.x, end.x) && near(t.y, 0) && near(t.z, end.z) && near(t.yaw, end.yaw)
          ? null
          : `OUT of a curve must sit on the end of the arc (${end.x.toFixed(3)}, 0, ${end.z.toFixed(3)}) with yaw ${end.yaw.toFixed(4)}`;
      }
      return near(t.x, 0) && near(t.y, 0) && near(t.z, definition.length) && near(t.yaw, 0)
        ? null
        : `OUT must be at (0, 0, ${definition.length}) with yaw 0`;
    }
    case ConnectorRole.Left:
      return near(t.x, -halfWidth) && near(t.y, 0) && near(t.z, pivotZ) && near(t.yaw, -QUARTER_TURN)
        ? null
        : `LEFT must be at (${-halfWidth}, 0, ${pivotZ}) with yaw −90°`;
    case ConnectorRole.Right:
      return near(t.x, halfWidth) && near(t.y, 0) && near(t.z, pivotZ) && near(t.yaw, QUARTER_TURN)
        ? null
        : `RIGHT must be at (${halfWidth}, 0, ${pivotZ}) with yaw +90°`;
    default:
      return 'exit must not have role IN';
  }
}

/**
 * Curves turn left/right by at most 90° (the generator keeps every heading within ±90° of the
 * start direction) and their inner side ground must not fold over the arc centre. Enclosures
 * (tunnel, interior) lie inside the module.
 */
function checkCurveAndEnclosure(definition: SegmentDefinition): string[] {
  const errors: string[] = [];
  const isCurveKind = definition.kind === SegmentKind.CurveLeft || definition.kind === SegmentKind.CurveRight;
  const curve = definition.curve;
  if (isCurveKind && !curve) errors.push('curve kinds need curve data (turn angle)');
  if (!isCurveKind && curve) errors.push(`kind ${definition.kind} must not define a curve`);
  if (curve) {
    const expectedSign = definition.kind === SegmentKind.CurveLeft ? -1 : 1;
    if (Math.sign(curve.angle) !== expectedSign) errors.push(`${definition.kind} needs a turn angle with sign ${expectedSign > 0 ? '+' : '−'}`);
    if (Math.abs(curve.angle) > QUARTER_TURN + EPSILON) errors.push('curves turn by at most 90°');
    if (arcRadius(definition.length, curve.angle) <= definition.width) errors.push('curve radius must exceed the track width');
    if (definition.junction) errors.push('curves must not define a junction pivot');
  }
  const enclosure = definition.enclosure;
  if (enclosure && !(enclosure.zFrom >= -EPSILON && enclosure.zTo <= definition.length + EPSILON && enclosure.zTo > enclosure.zFrom)) {
    errors.push(`enclosure must lie within z = 0 … ${definition.length}`);
  }
  return errors;
}

/**
 * Designed layouts: a container (walkable top reached by a double jump) keeps the run-up its roof needs
 * (level/terrain/roofRunUp.ts) free inside the module – of the layout's other obstacles, of terrain and of
 * the ground its moving hazards need. The populator would swap such a container for another obstacle of its
 * type every time; the part of the run-up before the module is checked there, at generation. The same for
 * the drop zone behind the roof inside the module (the populator leaves such an obstacle out).
 */
function validateRoofRunUps(definition: SegmentDefinition, rules: LayoutRules, config: HazardsConfig): string[] {
  const errors: string[] = [];
  const allHazards = (definition.hazards ?? []).map((h) => h.id);
  const terrain = terrainStretches(definition, rules);
  const layouts: { readonly id: string; readonly placements: readonly FixedObstaclePlacement[]; readonly hazards?: readonly string[] }[] = [
    ...(definition.fixedObstacles ? [{ id: 'fixedObstacles', placements: definition.fixedObstacles }] : []),
    ...(definition.obstacleVariants ?? []),
  ];
  for (const layout of layouts) {
    const placed = layout.placements.flatMap((placement) => {
      const point = definition.obstacleSpawnPoints.find((p) => p.id === placement.spawnPointId);
      const obstacle = DEFAULT_OBSTACLE_DEFINITIONS.get(placement.obstacleId);
      return point && obstacle ? [{ point, obstacle }] : []; // unknown ids: reported by validateLayouts
    });
    const hazards = (layout.hazards ?? allHazards).flatMap((id) => {
      const hazard = definition.hazards?.find((h) => h.id === id);
      return hazard ? hazardStretches(hazard, config, definition.lanes.length, rules).map((stretch) => ({ stretch, what: hazard.id })) : [];
    });
    for (const { point, obstacle } of placed) {
      const climb = roofClimbOf(obstacle, rules);
      if (!climb) continue;
      const needed = runUpStretch(obstacle, climb, point.lane, point.position.z);
      const busy = [
        ...placed.filter((p) => p.point !== point).map((p) => ({ stretch: obstacleStretch(p.obstacle, p.point.lane, p.point.position.z, rules), what: `${p.obstacle.id} on ${p.point.id}` })),
        ...terrain.map((stretch) => ({ stretch, what: 'terrain' })),
        ...hazards,
      ];
      for (const { stretch, what } of busy) {
        if (stretchesOverlap(stretch, needed)) {
          errors.push(`${layout.id}: ${what} stands in the run-up of ${obstacle.id} on ${point.id} (keep ${climb.runUp.toFixed(1)} m of plain road before its face)`);
        }
      }
      // … and nothing stands in the drop zone behind it: a runner off its roof lands there.
      const drop = dropZoneStretch(obstacle, climb, point.lane, point.position.z);
      const inTheWay = [
        ...placed.filter((p) => p.point !== point).map((p) => ({ stretch: obstacleFootprint(p.obstacle, p.point.lane, p.point.position.z), what: `${p.obstacle.id} on ${p.point.id}` })),
        ...terrain.map((stretch) => ({ stretch, what: 'terrain' })),
        ...hazards,
      ];
      for (const { stretch, what } of inTheWay) {
        if (stretchesOverlap(stretch, drop)) {
          errors.push(`${layout.id}: ${what} stands in the drop zone of ${obstacle.id} on ${point.id} (keep ${climb.dropZone.toFixed(1)} m behind its rear edge free)`);
        }
      }
    }
  }
  return errors;
}
